'use client';

import { useEffect, useMemo, useState } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import {
  BodyDefault,
  DrawerCustomSection,
  Skeleton,
  useThemeMode,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import {
  Background,
  BackgroundVariant,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  type ColorMode,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type {
  AssetGraphResponseDto,
  ExternalNetworkEntityKind,
} from '@terra/shared-contracts-external-network';
import {
  GRAPH_COLORS,
  buildFlowElements,
  externalNetworkEdgeTypes,
  externalNetworkNodeTypes,
  useAutoLayout,
  type AssetNodeData,
  type ExternalNetworkGraphVantagePoint,
} from '@terra/ui-graphs';
import {
  getGraphEdgeTranslationKey,
  isRelatedRouteEntityKind,
  listRelatedRouteAssets,
  type RelatedRouteAsset,
} from '@terra/ui-graphs/external-network';
import { useMainContentTabs } from '../../../store/layoutStore';
import { useExternalNetworkGraph } from '../../../hooks/api/useExternalNetworkGraph';
import { useExternalNetworkGraphVantagePoint } from '../../../hooks/api/useExternalNetworkGraphVantagePoint';
import { buildExternalNetworkRouteGraphQuery } from '../../../utils/external-network-graph.utils';
import { buildExternalNetworkAssetTab } from './ExternalNetworkAssetsList';
import { AssetRelatedAssetsTable } from './AssetRelatedAssetsTable';
import {
  AssetRouteViewToggle,
  type AssetRouteView,
} from './AssetRouteViewToggle';

const GRAPH_HEIGHT = 420;

type RouteFlowProps = {
  graph: AssetGraphResponseDto;
  selectedNodeId: string;
  vantagePoint: ExternalNetworkGraphVantagePoint;
};

function RouteFlow({ graph, selectedNodeId, vantagePoint }: RouteFlowProps) {
  const t = useTranslations('externalNetwork');
  const { openTab } = useMainContentTabs();
  const { mode: themeMode } = useThemeMode();
  const colorMode: ColorMode = themeMode === 'dark' ? 'dark' : 'light';
  const { flowNodes, flowEdges } = useMemo(
    () =>
      buildFlowElements(
        graph,
        (edgeType) => {
          const translationKey = getGraphEdgeTranslationKey(edgeType);

          return translationKey ? t(translationKey) : edgeType;
        },
        {
          includeInternetNode: true,
          selectedNodeId,
          orientEdgesFromInternet: true,
          vantagePoint,
        },
      ),
    [graph, selectedNodeId, t, vantagePoint],
  );
  const [nodes, setNodes, onNodesChange] =
    useNodesState<Node<AssetNodeData>>(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);
  const { isLayoutPending } = useAutoLayout<AssetNodeData>({
    layout: 'hierarchy',
    rankdir: 'LR',
    nodesep: 120,
    ranksep: 240,
    fitViewPadding: 0.3,
    fitViewMaxZoom: 1.2,
  });

  return (
    <ReactFlow
      colorMode={colorMode}
      nodes={nodes}
      edges={edges}
      style={{ visibility: isLayoutPending ? 'hidden' : 'visible' }}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={externalNetworkNodeTypes}
      edgeTypes={externalNetworkEdgeTypes}
      onNodeClick={(_event, node) => {
        if (node.id === selectedNodeId) {
          return;
        }

        if (node.data.entityKind === 'WEAKNESS') {
          openTab({
            id: `signal-${node.id}`,
            title: node.data.label || node.id,
            type: 'signal',
            data: { signalId: node.id },
          });

          return;
        }

        if (isRelatedRouteEntityKind(node.data.entityKind)) {
          openTab(
            buildExternalNetworkAssetTab({
              entityKind: node.data.entityKind,
              entityId: node.id,
              label: node.data.label || node.id,
            }),
          );
        }
      }}
      minZoom={0.3}
      maxZoom={5}
      proOptions={{ hideAttribution: true }}
      attributionPosition="bottom-left"
      defaultEdgeOptions={{ type: 'surfaceEdge', animated: false }}
    >
      <Background
        variant={BackgroundVariant.Dots}
        color={GRAPH_COLORS.background}
        gap={12}
      />
      <Controls />
    </ReactFlow>
  );
}

export function ExternalNetworkAssetRouteGraph({
  surfaceId,
  entityId,
  entityKind,
}: {
  surfaceId: string;
  entityId: string;
  entityKind: ExternalNetworkEntityKind;
}) {
  const t = useTranslations('externalNetwork');
  const tRelated = useTranslations('designSystem.signals.drawer');
  const { openTab } = useMainContentTabs();
  const [view, setView] = useState<AssetRouteView>('graph');
  const { graph, isLoading } = useExternalNetworkGraph({
    surfaceId,
    query: buildExternalNetworkRouteGraphQuery({
      id: entityId,
      kind: entityKind,
    }),
  });
  const vantagePoint = useExternalNetworkGraphVantagePoint(surfaceId);
  const relatedAssets = useMemo(
    () =>
      graph
        ? listRelatedRouteAssets({
            graph,
            selectedNodeId: entityId,
          })
        : [],
    [entityId, graph],
  );

  const handleOpenRelatedAsset = (asset: RelatedRouteAsset) => {
    if (!isRelatedRouteEntityKind(asset.kind)) {
      return;
    }

    openTab(
      buildExternalNetworkAssetTab({
        entityKind: asset.kind,
        entityId: asset.id,
        label: asset.label,
      }),
    );
  };

  if (!entityId) {
    return null;
  }

  if (isLoading) {
    return (
      <DrawerCustomSection title={t('assetDrawer.assetGraph')}>
        <Skeleton h={`${GRAPH_HEIGHT}px`} w="full" borderRadius="lg" />
      </DrawerCustomSection>
    );
  }

  if (!graph || graph.nodes.length === 0) {
    return null;
  }

  return (
    <DrawerCustomSection
      title={
        view === 'table'
          ? tRelated('relatedAssets')
          : t('assetDrawer.assetGraph')
      }
      action={<AssetRouteViewToggle view={view} onViewChange={setView} />}
    >
      <VStack gap={4} alignItems="stretch" w="full">
        {view === 'graph' ? (
          <BodyDefault color="fg.muted">
            {t('assetDrawer.assetGraphDescription')}
          </BodyDefault>
        ) : null}
        {view === 'table' ? (
          <AssetRelatedAssetsTable
            assets={relatedAssets}
            onOpenAsset={handleOpenRelatedAsset}
          />
        ) : (
          <Box
            w="full"
            h={GRAPH_HEIGHT}
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="lg"
            overflow="hidden"
          >
            <ReactFlowProvider key={entityId}>
              <RouteFlow
                graph={graph}
                selectedNodeId={entityId}
                vantagePoint={vantagePoint}
              />
            </ReactFlowProvider>
          </Box>
        )}
      </VStack>
    </DrawerCustomSection>
  );
}
